/**
 * useVotingAnnouncement
 *
 * Listens to the voting_results collection for the most recent closed period
 * that has been "announced" (results ready) and returns it if the current
 * user hasn't dismissed the announcement yet.
 *
 * Dismissal is tracked per-user per-period in localStorage:
 *   voting_announcement_seen:<userId>:<votingPeriodId>
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where, limit } from 'firebase/firestore';
import { db } from '@/firebase';
import type { VotingResults } from '@/types';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';

const SEEN_KEY_PREFIX = 'voting_announcement_seen:';

function getSeenKey(userId: string, votingPeriodId: string) {
  return `${SEEN_KEY_PREFIX}${userId}:${votingPeriodId}`;
}

export function useVotingAnnouncement(
  userId: string | null,
  tenantId?: string
): {
  announcement: VotingResults | null;
  isOpen: boolean;
  dismiss: () => void;
} {
  const [latest, setLatest] = useState<VotingResults | null>(null);
  const [dismissedPeriodId, setDismissedPeriodId] = useState<string | null>(null);

  // Subscribe to the newest closed+announced voting result for this tenant.
  useEffect(() => {
    if (!userId) {
      setLatest(null);
      return;
    }

    const baseCol = collection(db, 'voting_results');
    // Union read (NOT strict ==) — see CLAUDE.md "tenantIdReadUnion".
    // Strict equality drops results written under a legacy tenantId,
    // which would silently suppress the winner announcement.
    // tenantId filter REQUIRED (no unfiltered fallback): the rules lock rejects
    // unfiltered list queries on voting_results ("rules are not filters").
    const q = query(
      baseCol,
      where('tenantId', 'in', tenantIdReadUnion(tenantId ?? getTenantIdSync())),
      orderBy('closedAt', 'desc'),
      limit(3)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Walk through newest-first, pick the first one with a real winner.
        for (const d of snapshot.docs) {
          const data = { id: d.id, ...d.data() } as VotingResults;
          if (data.winnerId && data.winnerName) {
            setLatest(data);
            return;
          }
        }
        setLatest(null);
      },
      (err) => {
        console.error('[voting] Failed to listen for announcements:', err);
        setLatest(null);
      }
    );

    return unsubscribe;
  }, [userId, tenantId]);

  // Check localStorage whenever the latest result changes, so navigating
  // between pages doesn't re-show the modal after dismiss.
  useEffect(() => {
    if (!userId || !latest?.votingPeriodId) return;
    try {
      const seen = localStorage.getItem(getSeenKey(userId, latest.votingPeriodId));
      if (seen === '1') {
        setDismissedPeriodId(latest.votingPeriodId);
      } else {
        setDismissedPeriodId(null);
      }
    } catch {
      setDismissedPeriodId(null);
    }
  }, [userId, latest?.votingPeriodId]);

  const dismiss = useCallback(() => {
    if (!userId || !latest?.votingPeriodId) return;
    try {
      localStorage.setItem(getSeenKey(userId, latest.votingPeriodId), '1');
    } catch {
      // localStorage can throw in private mode — we still update state.
    }
    setDismissedPeriodId(latest.votingPeriodId);
  }, [userId, latest?.votingPeriodId]);

  const isOpen = useMemo(() => {
    if (!latest || !userId) return false;
    if (dismissedPeriodId === latest.votingPeriodId) return false;
    return true;
  }, [latest, userId, dismissedPeriodId]);

  return { announcement: latest, isOpen, dismiss };
}
